import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useRef, useState, useEffect } from "react";
import { IInboxStore } from "../../../models/inbox/InboxStore";
import { IInboxFilter } from "../../../models/inbox/InboxFilter";
import { ExpandedFilters } from "./expandedFilters";
import styles from "./filters.css";
import { useStore } from "../../../models";
import { SimpleIndicator } from "@fidget/common/components/indicators/simpleIndicator/simpleIndicator";
import { InboxSorting } from "@closerplatform/spinner-openapi";

export const Filters = observer(() => {
  const { uiStore } = useStore();
  const [inboxStore, inboxHeader, fitFiltersNumber] = useFiltersState();

  const renderSortingButton = () => {
    const { selectedSorting } = inboxStore;
    const isActive = selectedSorting === InboxSorting.Newest;

    return (
      <button
        type="button"
        className={classNames(styles["sorting-button"], { [styles["sorting-active"]]: isActive })}
        onClick={inboxStore.switchSorting}
      >
        <i className={`icon-arrow-up ${styles.arrow}`} />
      </button>
    );
  };

  const render = () => {
    const filters: Array<IInboxFilter> = inboxStore.permittedFilters;

    return (
      <div className={styles["inbox-header"]} ref={(el) => (inboxHeader.current = el)}>
        {renderSortingButton()}
        {filters.slice(0, fitFiltersNumber).map((filter: IInboxFilter) => (
          <div
            key={filter.id}
            className={classNames(styles.pill, { [styles.active]: filter.isSelected })}
            onClick={() => inboxStore.selectFilter(filter.id)}
            id={filter.id + "_filter"}
          >
            {filter.isUnreadEntriesCountDisplayed && (
              <>
                {filter.unreadCount}
                <i className={classNames("icon-person", styles["icon-person"])} />
              </>
            )}
            {filter.label}
            {!!filter.unreadMessagesCount && <SimpleIndicator count={filter.unreadMessagesCount} />}
          </div>
        ))}
        <ExpandedFilters
          onSelectFilter={inboxStore.selectFilter}
          filters={filters.slice(fitFiltersNumber)}
        />
      </div>
    );
  };

  if (!uiStore.canDisplayAllConversations && !uiStore.canDisplayOnlyOwnConversations) {
    return <div className={styles["inbox-header"]} />;
  }

  return render();
});

type FiltersStateType = [
  IInboxStore,
  React.MutableRefObject<HTMLDivElement | null | undefined>,
  number
];

const useFiltersState = (): FiltersStateType => {
  const { inboxStore } = useStore();

  const inboxHeader = useRef<HTMLDivElement | null | undefined>();
  const [fitFiltersNumber, setFitFiltersNumber] = useState(3);

  const onResize = () => {
    if (inboxHeader.current) {
      const isWidthBelowBreakpoint = inboxHeader.current.clientWidth < 360;

      if (isWidthBelowBreakpoint) {
        const { children } = inboxHeader.current;
        if (fitFiltersNumber === 3) {
          const spaceBetween =
            children[1]!.getBoundingClientRect().left - children[0]!.getBoundingClientRect().right;
          if (spaceBetween < 5) {
            setFitFiltersNumber(2);
          }
        } else {
          setFitFiltersNumber(2);
        }
      } else {
        setFitFiltersNumber(3);
      }
    }
  };

  useEffect(() => {
    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    onResize();
  });

  return [inboxStore, inboxHeader, fitFiltersNumber];
};
